import { useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';
import {
  loginState,
  loginType,
} from '../../../../services/state/login-state.jsx';
import { CATALYST_BACKOFFICEUSER_ROLES } from '../../../../utils/constants.js';

import {
  turnstileStatusState,
  turnstileStatusStateStatuses,
  TurnstileWidget,
} from '../../../turnstile-widget.jsx';
import { Button } from '../../../ui/button.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form.jsx';
import { PasswordInput } from '../../../ui/input-password.jsx';
import { Input } from '../../../ui/input.jsx';

export const FormLoginWithPassword = () => {
  const navigate = useNavigate();
  const formRef = useRef();
  const turnstileRef = useRef();
  const { status: turnstileStatus, setStatus: setTurnstileStatus } =
    useSnapshot(turnstileStatusState);

  const formLoginWithPassword = useForm({
    resolver: yupResolver(
      yup.object().shape({
        email: yup
          .string()
          .min(1, 'Por favor insira o seu email')
          .email('Email inválido')
          .required(),
        password: yup
          .string()
          .min(1, 'Por favor insira a sua password')
          .required(),
        rememberMe: yup.boolean(),
      }),
    ),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const mutationLoginWithPassword = useMutation({
    mutationFn: async (data) => {
      const { data: loggedInUser } = await msCatalystApi.post(
        `/auth/login/password`,
        {
          email: data.email,
          password: data.password,
          turnstile_token: data.turnstileToken,
        },
      );

      return loggedInUser.data;
    },
    onSuccess: (data) => {
      authState.user = data;
      authState.isAdmin =
        data.details.role === CATALYST_BACKOFFICEUSER_ROLES.admin;

      if (authState.isAdmin) {
        navigate('/backoffice');
      } else {
        navigate('/');
      }
    },
    onError: (error) => {
      toast.error('Erro ao realizar o login', {
        description:
          'Por favor verifique que introduziu as suas credências corretamente',
      });

      setTurnstileStatus('pending');
      turnstileRef.current?.reset();
    },
  });

  async function onSubmit(data) {
    const formData = new FormData(formRef.current);
    const turnstileToken = formData.get('cf-turnstile-response');

    if (!turnstileToken) {
      toast.error('Erro ao realizar o login', {
        description:
          'Não foi possível determinar com sucesso a sua veracidade como humano',
      });

      return;
    }

    mutationLoginWithPassword.mutate({
      email: data.email,
      password: data.password,
      turnstileToken,
    });
  }

  return (
    <Form {...formLoginWithPassword}>
      <form
        ref={formRef}
        name="login-password"
        onSubmit={formLoginWithPassword.handleSubmit(onSubmit)}
      >
        <div className="grid gap-3">
          <FormField
            control={formLoginWithPassword.control}
            name="email"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={formLoginWithPassword.formState.isSubmitting}
                    type="email"
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={formLoginWithPassword.control}
            name="password"
            render={({ field }) => (
              <FormItem variant="portal">
                <FormLabel>Palavra-passe</FormLabel>
                <FormControl>
                  <PasswordInput
                    {...field}
                    disabled={formLoginWithPassword.formState.isSubmitting}
                    autoComplete="off"
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-start">
            <Button
              asChild
              variant="link"
              type="button"
              className="h-auto px-1 text-xs font-medium text-muted-foreground hover:opacity-75"
            >
              <Link to="/auth/password/recover">
                Esqueceu-se da sua palavra-passe?
              </Link>
            </Button>
          </div>

          <div className="pt-2">
            <TurnstileWidget ref={turnstileRef} />
          </div>
        </div>

        <div className="flex flex-col items-center pt-8">
          <Button
            type="submit"
            variant="portal"
            className="w-full"
            disabled={
              !formLoginWithPassword.formState.isDirty ||
              formLoginWithPassword.formState.isSubmitting ||
              mutationLoginWithPassword.isPending ||
              turnstileStatus !== turnstileStatusStateStatuses.SOLVED
            }
            isLoading={
              formLoginWithPassword.formState.isSubmitting ||
              mutationLoginWithPassword.isPending
            }
          >
            Iniciar sessão
          </Button>

          <div className="flex flex-col gap-y-1 pt-6">
            <Button
              variant="link"
              type="button"
              onClick={() => {
                loginState.type = loginType.otp;
              }}
              className="h-auto text-left text-xs font-medium text-muted-foreground hover:opacity-75"
            >
              Iniciar sessão com código único de acesso via e-mail
            </Button>

            <Button
              asChild
              variant="link"
              type="button"
              className="h-auto text-left text-xs font-medium text-muted-foreground hover:opacity-75"
            >
              <Link to="/auth/register">
                Não possui uma conta? Registe-se aqui
              </Link>
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};
